import { createHttpService } from "../ApiService";
import type {
  TFeeComponentApi,
  TCreateFeeComponentApiRequest,
  TUpdateFeeComponentApiRequest,
  TGetClassFeesResponse,
  TStudentCategory,
  TStudentCategoriesListResponse,
  TCreateStudentCategoryRequest,
  TUpdateStudentCategoryRequest,
  TCategoryComponentAttachment,
  TCategoryComponentAttachmentsListResponse,
  TCreateCategoryComponentAttachmentRequest,
  TUpdateCategoryComponentAttachmentRequest,
} from "./feeStructure.interfaces";
import {
  buildClassFeesMock,
  feeComponentsMock,
  studentCategoriesByClassMock,
  categoryAttachmentsMock,
  pushFeeComponentMock,
  upsertStudentCategoriesForClass,
  setAttachmentsForCategory,
  nextFeeComponentId,
  nextCategoryId,
  nextRuleId,
  nestedForComponent,
} from "../../__mocks__/feeStructure/feeStructureMock";
import { localStorageGet } from "../../utils/localStorage";

function getFeeStructureInstituteId(): string {
  const stored = localStorageGet("selectedInstitute");
  if (
    stored &&
    typeof stored === "object" &&
    !Array.isArray(stored) &&
    typeof (stored as { id?: unknown }).id === "string" &&
    (stored as { id: string }).id.length > 0
  ) {
    return (stored as { id: string }).id;
  }
  throw new Error(
    "selectedInstitute in localStorage is required for fee structure API calls.",
  );
}

const unwrapFeeStructureResponse = <T>(raw: unknown): T => {
  if (raw === null || raw === undefined) {
    return raw as T;
  }
  if (typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
    const inner = (raw as { data: unknown }).data;
    if (inner !== undefined) {
      return inner as T;
    }
  }
  return raw as T;
};

const rejectWithFeeStructureApiMessage = (e: unknown): never => {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data != null && typeof data === "object" && !Array.isArray(data)) {
    const top = (data as { message?: unknown }).message;
    if (typeof top === "string" && top.trim() !== "") {
      throw new Error(top);
    }
    const inner = (data as { data?: unknown }).data;
    if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
      const nested = (inner as { message?: unknown }).message;
      if (typeof nested === "string" && nested.trim() !== "") {
        throw new Error(nested);
      }
    }
  }
  throw e;
};

export const useFeeStructureService = () => {
  const httpAPI = createHttpService();
  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  const qp = () => {
    const instituteId = getFeeStructureInstituteId();
    return { params: { instituteId } };
  };

  const getClassFees = (opts?: {
    classId?: string;
    academicYear?: string;
  }) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      return Promise.resolve(
        buildClassFeesMock({
          instituteId,
          classId: opts?.classId,
          academicYear: opts?.academicYear,
        }),
      );
    }
    return httpAPI.feeStructure
      .get<unknown>("/class-fees", {
        params: {
          instituteId,
          ...(opts?.classId ? { classId: opts.classId } : {}),
          ...(opts?.academicYear
            ? { academicYear: opts.academicYear }
            : {}),
        },
      })
      .then((raw) => unwrapFeeStructureResponse<TGetClassFeesResponse>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const listFeeComponents = (opts?: { activeOnly?: boolean }) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      let rows = feeComponentsMock.map((r) => ({
        ...r,
        instituteId,
      }));
      if (opts?.activeOnly) {
        rows = rows.filter((r) => r.isActive && r.status === "active");
      }
      return Promise.resolve(rows);
    }
    return httpAPI.feeStructure
      .get<unknown>("/components", {
        params: {
          instituteId,
          ...(opts?.activeOnly != null
            ? { activeOnly: String(opts.activeOnly) }
            : {}),
        },
      })
      .then((raw) => unwrapFeeStructureResponse<TFeeComponentApi[]>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const getFeeComponentById = (id: string) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const row = feeComponentsMock.find((r) => r.id === id);
      if (!row) return Promise.reject(new Error("Fee component not found"));
      return Promise.resolve({ ...row, instituteId });
    }
    return httpAPI.feeStructure
      .get<unknown>(`/components/${id}`, qp())
      .then((raw) => unwrapFeeStructureResponse<TFeeComponentApi>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const createFeeComponent = (request: TCreateFeeComponentApiRequest) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const row: TFeeComponentApi = {
        id: nextFeeComponentId(),
        name: request.name,
        feeType: request.feeType,
        description: request.description,
        amount: request.amount,
        currency: request.currency ?? "INR",
        frequency: request.frequency,
        isActive: true,
        status: request.status ?? "active",
        instituteId,
        createdAt: new Date().toISOString(),
      };
      pushFeeComponentMock(row);
      return Promise.resolve(row);
    }
    return httpAPI.feeStructure
      .post<unknown>("/components", request, qp())
      .then((raw) => unwrapFeeStructureResponse<TFeeComponentApi>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const updateFeeComponent = (
    id: string,
    request: TUpdateFeeComponentApiRequest,
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const idx = feeComponentsMock.findIndex((r) => r.id === id);
      if (idx === -1) return Promise.reject(new Error("Fee component not found"));
      feeComponentsMock[idx] = {
        ...feeComponentsMock[idx],
        ...request,
        instituteId,
      };
      return Promise.resolve(feeComponentsMock[idx]);
    }
    return httpAPI.feeStructure
      .patch<unknown>(`/components/${id}`, request, qp())
      .then((raw) => unwrapFeeStructureResponse<TFeeComponentApi>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const deleteFeeComponent = (id: string) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const idx = feeComponentsMock.findIndex((r) => r.id === id);
      if (idx === -1) return Promise.reject(new Error("Fee component not found"));
      feeComponentsMock.splice(idx, 1);
      return Promise.resolve(undefined);
    }
    return httpAPI.feeStructure
      .delete<unknown>(`/components/${id}`, undefined, qp())
      .then(() => undefined)
      .catch(rejectWithFeeStructureApiMessage);
  };

  const listStudentCategoriesForClass = (
    classId: string,
    opts?: { page?: number; limit?: number },
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const items = (studentCategoriesByClassMock[classId] ?? []).map((c) => ({
        ...c,
        instituteId,
      }));
      const page = opts?.page ?? 1;
      const limit = opts?.limit ?? 50;
      const slice = items.slice((page - 1) * limit, page * limit);
      const out: TStudentCategoriesListResponse = {
        items: slice,
        total: items.length,
        page,
        limit,
      };
      return Promise.resolve(out);
    }
    return httpAPI.feeStructure
      .get<unknown>(`/classes/${classId}/categories`, {
        params: {
          instituteId,
          ...(opts?.page != null ? { page: opts.page } : {}),
          ...(opts?.limit != null ? { limit: opts.limit } : {}),
        },
      })
      .then((raw) =>
        unwrapFeeStructureResponse<TStudentCategoriesListResponse>(raw),
      )
      .catch(rejectWithFeeStructureApiMessage);
  };

  const createStudentCategoryForClass = (
    classId: string,
    body: TCreateStudentCategoryRequest,
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const row: TStudentCategory = {
        id: nextCategoryId(),
        instituteId,
        classId,
        name: body.name,
        description: body.description,
        code: body.code,
        precedence: body.precedence,
        isDefault: body.isDefault,
        eligibilityRules: body.eligibilityRules,
        requiredDocumentTypes: body.requiredDocumentTypes,
        isActive: true,
        createdAt: new Date().toISOString(),
      };
      const prev = studentCategoriesByClassMock[classId] ?? [];
      if (body.isDefault) {
        prev.forEach((c) => {
          c.isDefault = false;
        });
      }
      upsertStudentCategoriesForClass(classId, [...prev, row]);
      categoryAttachmentsMock[row.id] = [];
      return Promise.resolve(row);
    }
    return httpAPI.feeStructure
      .post<unknown>(`/classes/${classId}/categories`, body, qp())
      .then((raw) => unwrapFeeStructureResponse<TStudentCategory>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const getStudentCategoryById = (id: string) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      for (const rows of Object.values(studentCategoriesByClassMock)) {
        const hit = rows.find((c) => c.id === id);
        if (hit) return Promise.resolve({ ...hit, instituteId });
      }
      return Promise.reject(new Error("Student category not found"));
    }
    return httpAPI.feeStructure
      .get<unknown>(`/categories/${id}`, qp())
      .then((raw) => unwrapFeeStructureResponse<TStudentCategory>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const updateStudentCategory = (
    id: string,
    body: TUpdateStudentCategoryRequest,
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      for (const classId of Object.keys(studentCategoriesByClassMock)) {
        const rows = studentCategoriesByClassMock[classId] ?? [];
        const idx = rows.findIndex((c) => c.id === id);
        if (idx === -1) continue;
        if (body.isDefault) {
          rows.forEach((c) => {
            if (c.id !== id) c.isDefault = false;
          });
        }
        rows[idx] = { ...rows[idx], ...body };
        upsertStudentCategoriesForClass(classId, [...rows]);
        return Promise.resolve(rows[idx]);
      }
      return Promise.reject(new Error("Student category not found"));
    }
    return httpAPI.feeStructure
      .patch<unknown>(`/categories/${id}`, body, qp())
      .then((raw) => unwrapFeeStructureResponse<TStudentCategory>(raw))
      .catch(rejectWithFeeStructureApiMessage);
  };

  const deleteStudentCategory = (id: string) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      for (const classId of Object.keys(studentCategoriesByClassMock)) {
        const rows = studentCategoriesByClassMock[classId] ?? [];
        const idx = rows.findIndex((c) => c.id === id);
        if (idx === -1) continue;
        rows.splice(idx, 1);
        upsertStudentCategoriesForClass(classId, [...rows]);
        delete categoryAttachmentsMock[id];
        return Promise.resolve({ deleted: true as const });
      }
      return Promise.reject(new Error("Student category not found"));
    }
    return httpAPI.feeStructure
      .delete<unknown>(`/categories/${id}`, undefined, qp())
      .then((raw) =>
        unwrapFeeStructureResponse<{ deleted: boolean }>(raw),
      )
      .catch(rejectWithFeeStructureApiMessage);
  };

  const listCategoryComponentAttachments = (categoryId: string) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const items = categoryAttachmentsMock[categoryId] ?? [];
      const out: TCategoryComponentAttachmentsListResponse = { items };
      return Promise.resolve(out);
    }
    return httpAPI.feeStructure
      .get<unknown>(`/categories/${categoryId}/components`, qp())
      .then((raw) =>
        unwrapFeeStructureResponse<TCategoryComponentAttachmentsListResponse>(
          raw,
        ),
      )
      .catch(rejectWithFeeStructureApiMessage);
  };

  const createCategoryComponentAttachment = (
    categoryId: string,
    body: TCreateCategoryComponentAttachmentRequest,
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const fc = feeComponentsMock.find((c) => c.id === body.componentTypeId);
      if (!fc) {
        return Promise.reject(new Error("Fee component not found for institute"));
      }
      const row: TCategoryComponentAttachment = {
        id: nextRuleId(),
        categoryId,
        componentTypeId: body.componentTypeId,
        ruleType: body.ruleType,
        value: body.value,
        maxCap: body.maxCap,
        minAmount: body.minAmount,
        stackingPolicy: body.stackingPolicy,
        priority: body.priority ?? 0,
        notes: body.notes,
        isActive: true,
        componentType: nestedForComponent(fc),
        createdAt: new Date().toISOString(),
      };
      const prev = categoryAttachmentsMock[categoryId] ?? [];
      if (prev.some((p) => p.componentTypeId === body.componentTypeId)) {
        return Promise.reject(
          new Error("This fee component is already attached to the category."),
        );
      }
      setAttachmentsForCategory(categoryId, [...prev, row]);
      return Promise.resolve(row);
    }
    return httpAPI.feeStructure
      .post<unknown>(`/categories/${categoryId}/components`, body, qp())
      .then((raw) =>
        unwrapFeeStructureResponse<TCategoryComponentAttachment>(raw),
      )
      .catch(rejectWithFeeStructureApiMessage);
  };

  const updateCategoryComponentAttachment = (
    categoryId: string,
    ruleId: string,
    body: TUpdateCategoryComponentAttachmentRequest,
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const prev = categoryAttachmentsMock[categoryId] ?? [];
      const idx = prev.findIndex((r) => r.id === ruleId);
      if (idx === -1) return Promise.reject(new Error("Rule not found"));
      let comp = prev[idx].componentType;
      if (body.componentTypeId && body.componentTypeId !== prev[idx].componentTypeId) {
        const fc = feeComponentsMock.find((c) => c.id === body.componentTypeId);
        if (!fc) return Promise.reject(new Error("Fee component not found"));
        comp = nestedForComponent(fc);
      }
      prev[idx] = {
        ...prev[idx],
        ...body,
        componentType: comp,
      };
      setAttachmentsForCategory(categoryId, [...prev]);
      return Promise.resolve(prev[idx]);
    }
    return httpAPI.feeStructure
      .patch<unknown>(
        `/categories/${categoryId}/components/${ruleId}`,
        body,
        qp(),
      )
      .then((raw) =>
        unwrapFeeStructureResponse<TCategoryComponentAttachment>(raw),
      )
      .catch(rejectWithFeeStructureApiMessage);
  };

  const deleteCategoryComponentAttachment = (
    categoryId: string,
    ruleId: string,
  ) => {
    const instituteId = getFeeStructureInstituteId();
    if (appWithMockData) {
      const prev = categoryAttachmentsMock[categoryId] ?? [];
      const next = prev.filter((r) => r.id !== ruleId);
      if (next.length === prev.length) {
        return Promise.reject(new Error("Rule not found"));
      }
      setAttachmentsForCategory(categoryId, next);
      return Promise.resolve({ deleted: true as const });
    }
    return httpAPI.feeStructure
      .delete<unknown>(
        `/categories/${categoryId}/components/${ruleId}`,
        undefined,
        qp(),
      )
      .then((raw) =>
        unwrapFeeStructureResponse<{ deleted: boolean }>(raw),
      )
      .catch(rejectWithFeeStructureApiMessage);
  };

  return {
    getClassFees,
    listFeeComponents,
    getFeeComponentById,
    createFeeComponent,
    updateFeeComponent,
    deleteFeeComponent,
    listStudentCategoriesForClass,
    createStudentCategoryForClass,
    getStudentCategoryById,
    updateStudentCategory,
    deleteStudentCategory,
    listCategoryComponentAttachments,
    createCategoryComponentAttachment,
    updateCategoryComponentAttachment,
    deleteCategoryComponentAttachment,
  };
};
